import classNames from 'classnames';

import {
  benchPicks,
  setCaptain,
  setViceCaptain,
  startingPicks,
  swapPlayerIntoSlot,
  tradeSlots
} from '@lib/live/harness/edit';
import type { Roster, RosterPlayer } from '@lib/live/harness/roster';
import {
  FIRST_BENCH_SLOT,
  type HarnessPick,
  type HarnessSquad
} from '@lib/live/harness/squad';

type SquadEditorProps = {
  squad: HarnessSquad;
  rosters: Roster[];
  onChange: (squad: HarnessSquad) => void;
};

const allPlayers = (rosters: Roster[]): RosterPlayer[] => {
  const byOptaId = new Map<string, RosterPlayer>();
  for (const roster of rosters) {
    for (const player of roster.players) {
      if (!byOptaId.has(player.optaId)) byOptaId.set(player.optaId, player);
    }
  }
  return Array.from(byOptaId.values());
};

/**
 * One squad slot: who is in it, whether they start, and the armbands.
 *
 * The replacement list is filtered to the slot's position. Swapping across
 * positions is possible through the squad shape rules, but doing it from a
 * dropdown that offers every player makes it far too easy to build a squad
 * that fails validation for a reason nobody intended.
 */
const SlotRow = ({
  pick,
  squad,
  rosters,
  players,
  onChange
}: {
  pick: HarnessPick;
  squad: HarnessSquad;
  rosters: Roster[];
  players: RosterPlayer[];
  onChange: (squad: HarnessSquad) => void;
}) => {
  const isCaptain = squad.captainSlot === pick.slot;
  const isViceCaptain = squad.viceCaptainSlot === pick.slot;
  const selectedOptaIds = new Set(squad.picks.map((one) => one.optaId));

  const replacements = players.filter(
    (player) =>
      player.position === pick.position &&
      (player.optaId === pick.optaId || !selectedOptaIds.has(player.optaId))
  );

  const isStarting = pick.slot < FIRST_BENCH_SLOT;
  // Trade with the first player on the other side sharing this position, or
  // whoever is first there if none does. The validator reports an illegal
  // formation afterwards, which is more useful than refusing the click and
  // leaving the operator to work out why.
  const otherSide = isStarting ? benchPicks(squad) : startingPicks(squad);
  const tradePartner =
    otherSide.find((one) => one.position === pick.position) ?? otherSide[0];

  return (
    <tr data-cy="harness-slot-row" data-cy-slot={pick.slot}>
      <td className="py-0.5 pr-2 text-xs text-neutral-500">{pick.slot}</td>
      <td className="py-0.5 pr-2 text-xs font-semibold">{pick.position}</td>
      <td className="py-0.5 pr-2">
        <select
          className="w-full max-w-[180px] rounded border border-neutral-300 px-1 py-0.5 text-xs"
          value={pick.optaId}
          aria-label={`Player in slot ${pick.slot}`}
          data-cy="harness-slot-player"
          onChange={(event) => {
            const player = players.find(
              (one) => one.optaId === event.target.value
            );
            if (player) {
              onChange(swapPlayerIntoSlot(squad, pick.slot, player, rosters));
            }
          }}
        >
          {replacements.map((player) => (
            <option key={player.optaId} value={player.optaId}>
              {player.displayName} ({player.minutesPlayed}m, {player.points}pt)
            </option>
          ))}
        </select>
      </td>
      <td className="py-0.5 pr-2">
        <button
          type="button"
          className="rounded border border-neutral-300 px-1.5 py-0.5 text-xs"
          disabled={!tradePartner}
          data-cy="harness-slot-trade"
          onClick={() => {
            if (tradePartner) {
              onChange(tradeSlots(squad, pick.slot, tradePartner.slot));
            }
          }}
        >
          {isStarting ? 'Bench' : 'Start'}
        </button>
      </td>
      <td className="py-0.5 pr-1">
        <button
          type="button"
          aria-label={`Make ${pick.displayName} captain`}
          data-cy="harness-slot-captain"
          className={classNames(
            'w-6 rounded border px-1 py-0.5 text-xs font-bold',
            isCaptain
              ? 'border-neutral-800 bg-neutral-800 text-white'
              : 'border-neutral-300'
          )}
          onClick={() => onChange(setCaptain(squad, pick.slot))}
        >
          C
        </button>
      </td>
      <td className="py-0.5">
        <button
          type="button"
          aria-label={`Make ${pick.displayName} vice-captain`}
          data-cy="harness-slot-vice-captain"
          className={classNames(
            'w-6 rounded border px-1 py-0.5 text-xs font-bold',
            isViceCaptain
              ? 'border-neutral-800 bg-neutral-800 text-white'
              : 'border-neutral-300'
          )}
          onClick={() => onChange(setViceCaptain(squad, pick.slot))}
        >
          V
        </button>
      </td>
    </tr>
  );
};

export const SquadEditor = ({ squad, rosters, onChange }: SquadEditorProps) => {
  const players = allPlayers(rosters);

  const renderRows = (picks: HarnessPick[]) =>
    picks.map((pick) => (
      <SlotRow
        key={pick.slot}
        pick={pick}
        squad={squad}
        rosters={rosters}
        players={players}
        onChange={onChange}
      />
    ));

  return (
    <div className="flex flex-col gap-2" data-cy="harness-squad-editor">
      <table className="w-full">
        <tbody>{renderRows(startingPicks(squad))}</tbody>
      </table>
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Bench
      </p>
      <table className="w-full">
        <tbody>{renderRows(benchPicks(squad))}</tbody>
      </table>
    </div>
  );
};
